import { useState, useEffect, useMemo, useCallback } from 'react';
import { Text, Badge, Button, Loader, Group, Table } from '@mantine/core';
import { IconRefresh } from '@tabler/icons-react';
import apiService from '../services/api';
import { useTranslation } from '../context/TranslationContext';
import { formatPrice } from '../utils/formatPrice';
import { parsePickupWindow, formatPickupDate } from '../utils/pickupDates';
import './AdminPickupBookings.css';

const DAY_ORDER = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const UNPARSED_WINDOW_SORT_KEY = Number.MAX_SAFE_INTEGER;

const STATUS_BADGE_COLORS = {
  pending: 'yellow',
  confirmed: 'blue',
  partially_shipped: 'orange',
  shipped: 'cyan',
  refunded: 'gray',
};

function windowSortKey(slot) {
  if (!slot.parsed) return UNPARSED_WINDOW_SORT_KEY;
  const dayIndex = DAY_ORDER.indexOf(slot.parsed.day);
  const [hours, minutes] = slot.parsed.start.split(':').map(Number);
  return (dayIndex === -1 ? DAY_ORDER.length : dayIndex) * 24 * 60 + hours * 60 + minutes;
}

function slotLabel(slot) {
  if (!slot.parsed) return slot.raw;
  const { start, end } = slot.parsed;
  return `${start}–${end}`;
}

function groupBookingsByLocation(bookings, labels) {
  const locations = new Map();
  for (const booking of bookings) {
    const locationKey = booking.location_id || '_unassigned';
    if (!locations.has(locationKey)) {
      locations.set(locationKey, {
        id: locationKey,
        name: booking.location_name || labels.unknownLocation,
        address: booking.location_address || '',
        dateGroups: new Map(),
      });
    }
    const location = locations.get(locationKey);
    // null date = booked before the date picker existed; grouped separately at the end
    const dateKey = booking.pickup_date || '_undated';
    if (!location.dateGroups.has(dateKey)) {
      location.dateGroups.set(dateKey, { date: booking.pickup_date || null, slots: new Map() });
    }
    const dateGroup = location.dateGroups.get(dateKey);
    const rawWindow = (booking.pickup_time || '').trim();
    if (!dateGroup.slots.has(rawWindow)) {
      dateGroup.slots.set(rawWindow, { raw: rawWindow, parsed: parsePickupWindow(rawWindow), bookings: [] });
    }
    dateGroup.slots.get(rawWindow).bookings.push(booking);
  }

  return [...locations.values()].map((location) => ({
    ...location,
    sortedDateGroups: [...location.dateGroups.values()]
      .sort((a, b) => {
        if (!a.date && b.date) return 1;
        if (a.date && !b.date) return -1;
        return (a.date || '').localeCompare(b.date || '');
      })
      .map((dateGroup) => ({
        ...dateGroup,
        sortedSlots: [...dateGroup.slots.values()].sort(
          (a, b) => windowSortKey(a) - windowSortKey(b) || a.raw.localeCompare(b.raw)
        ),
      })),
  }));
}

export default function AdminPickupBookings() {
  const { t, langCode } = useTranslation();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadBookings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.getPickupBookings();
      setBookings(data);
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadBookings(); }, [loadBookings]);

  const groupedLocations = useMemo(
    () => groupBookingsByLocation(bookings, {
      unknownLocation: t('unknownPickupLocation', 'Unknown location'),
      undatedLabel: t('pickupBookingsUndated', 'No date (booked before the date picker)'),
    }),
    [bookings, t]
  );

  const dateGroupHeading = (dateGroup) => (
    dateGroup.date
      ? formatPickupDate(dateGroup.date, langCode)
      : t('pickupBookingsUndated', 'No date (booked before the date picker)')
  );

  return (
    <div className="pickup-bookings-wrapper dashboard-view-wrapper">
      <header className="dashboard-page-header" style={{ marginBottom: '2rem' }}>
        <div>
          <h1 className="dashboard-page-title">{t('pickupBookings', 'Pickup Bookings')}</h1>
          <p className="dashboard-page-description">
            {t('pickupBookingsDescription', 'See who is coming at which pickup slot, grouped by location.')}
          </p>
        </div>
        <Button
          variant="default"
          leftSection={<IconRefresh size={14} aria-hidden="true" />}
          onClick={loadBookings}
          loading={loading}
        >
          {t('refresh', 'Refresh')}
        </Button>
      </header>

      {loading ? (
        <div className="pickup-bookings-state" role="status" aria-live="polite">
          <Loader size="sm" />
          <Text c="dimmed">{t('loading', 'Loading...')}</Text>
        </div>
      ) : error ? (
        <div className="pickup-bookings-state" role="alert">
          <Text c="red">{t('pickupBookingsError', 'Failed to load pickup bookings.')}</Text>
        </div>
      ) : groupedLocations.length === 0 ? (
        <div className="pickup-bookings-state" role="status">
          <Text c="dimmed">{t('noPickupBookings', 'No pickup bookings yet.')}</Text>
        </div>
      ) : (
        <div className="pickup-bookings-list" aria-live="polite">
          {groupedLocations.map((location) => (
            <section key={location.id} className="pickup-bookings-location" aria-label={location.name}>
              <div className="pickup-bookings-location-header">
                <h2>{location.name}</h2>
                {location.address && <p>{location.address}</p>}
              </div>

              {location.sortedDateGroups.map((dateGroup) => (
                <div key={dateGroup.date || '_undated'} className="pickup-bookings-date-group">
                  <h3>{dateGroupHeading(dateGroup)}</h3>

                  {dateGroup.sortedSlots.map((slot) => (
                    <div key={slot.raw} className="pickup-bookings-window">
                      <Group justify="space-between" wrap="nowrap" mb="xs">
                        <h4>
                          <span className="sr-only">{t('pickupWindow', 'Pickup window')}: </span>
                          {slotLabel(slot)}
                        </h4>
                        <Badge variant="light" color="indigo" size="sm">
                          {t('customersComing', '{count} coming', { count: slot.bookings.length })}
                        </Badge>
                      </Group>

                      <Table>
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th scope="col">{t('customer', 'Customer')}</Table.Th>
                            <Table.Th scope="col">{t('orderPlaced', 'Order placed')}</Table.Th>
                            <Table.Th scope="col">{t('total', 'Total')}</Table.Th>
                            <Table.Th scope="col">{t('status', 'Status')}</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {slot.bookings.map((booking) => (
                            <Table.Tr key={booking.order_id}>
                              <Table.Td>
                                <Text fw={500}>{booking.customer_name}</Text>
                                <Text size="xs" c="dimmed">{booking.customer_email}</Text>
                              </Table.Td>
                              <Table.Td>{new Date(booking.created_at).toLocaleDateString()}</Table.Td>
                              <Table.Td>{formatPrice(booking.total_price)}</Table.Td>
                              <Table.Td>
                                <Badge
                                  variant="light"
                                  color={STATUS_BADGE_COLORS[booking.status] || 'gray'}
                                  size="sm"
                                >
                                  {t(booking.status, booking.status)}
                                </Badge>
                              </Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>
                    </div>
                  ))}
                </div>
              ))}
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
